import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/axios';

const TRAIT_CATEGORIES = [
  { key: 'smoking', label: 'Smoking', group: 'Lifestyle', options: ['Never', 'Sometimes', 'Regularly'] },
  { key: 'drinking', label: 'Drinking', group: 'Lifestyle', options: ['Never', 'Socially', 'Regularly'] },
  { key: 'cannabis', label: 'Cannabis', group: 'Lifestyle', options: ['Never', 'Sometimes', 'Regularly'] },
  { key: 'diet', label: 'Diet', group: 'Lifestyle', options: ['Omnivore', 'Vegetarian', 'Vegan', 'Pescatarian', 'Keto', 'Halal', 'Kosher', 'Other'] },
  { key: 'exercise', label: 'Exercise', group: 'Lifestyle', options: ['Never', 'Sometimes', 'Active', 'Very active'] },
  { key: 'sleep_schedule', label: 'Sleep schedule', group: 'Lifestyle', options: ['Early bird', 'Night owl', 'Varies'] },
  { key: 'education', label: 'Education', group: 'Background', options: ['High school', 'Some college', 'Associate degree', "Bachelor's degree", "Master's degree", 'Doctorate', 'Trade school', 'Other'] },
  { key: 'religion', label: 'Religion', group: 'Background', options: ['Agnostic', 'Atheist', 'Buddhist', 'Catholic', 'Christian', 'Hindu', 'Jewish', 'Muslim', 'Sikh', 'Spiritual', 'Other', 'Prefer not to say'] },
  { key: 'politics', label: 'Political leaning', group: 'Background', options: ['Liberal', 'Moderate', 'Conservative', 'Apolitical', 'Other', 'Prefer not to say'] },
  { key: 'languages', label: 'Languages', group: 'Background', multiSelect: true, options: ['English', 'Spanish', 'French', 'German', 'Mandarin', 'Cantonese', 'Japanese', 'Korean', 'Arabic', 'Hindi', 'Portuguese', 'Italian', 'Russian', 'Tagalog', 'Vietnamese', 'ASL', 'Other'] },
  { key: 'ethnicity', label: 'Ethnicity', group: 'Background', multiSelect: true, options: ['Asian', 'Black / African American', 'Hispanic / Latino', 'Middle Eastern', 'Native American', 'Pacific Islander', 'White / Caucasian', 'Mixed', 'Other', 'Prefer not to say'] },
  { key: 'height', label: 'Height', group: 'Physical', options: ['Under 5\'0"', '5\'0" - 5\'3"', '5\'4" - 5\'7"', '5\'8" - 5\'11"', '6\'0" - 6\'3"', '6\'4" and above'] },
  { key: 'body_type', label: 'Body type', group: 'Physical', options: ['Slim', 'Athletic', 'Average', 'Curvy', 'Plus size', 'Prefer not to say'] },
  { key: 'has_kids', label: 'Has children', group: 'Physical', options: ['No', 'Yes, they live with me', "Yes, they don't live with me"] },
  { key: 'wants_kids', label: 'Wants children', group: 'Physical', options: ['Definitely', 'Open to it', 'Not sure', 'Probably not', 'Definitely not'] },
  { key: 'pets', label: 'Pets', group: 'Physical', multiSelect: true, options: ['Dog', 'Cat', 'Bird', 'Fish', 'Reptile', 'Other', 'No pets', 'Allergic to pets'] },
  { key: 'communication_style', label: 'Communication style', group: 'Personality', options: ['Big texter', 'Phone calls', 'Video chats', 'In person preferred', 'Memes and GIFs'] },
  { key: 'social_energy', label: 'Social energy', group: 'Personality', options: ['Introvert', 'Ambivert', 'Extrovert'] },
  { key: 'relationship_goal', label: 'Relationship goal', group: 'Personality', options: ['Casual', 'Short-term', 'Long-term', 'Marriage', 'Figuring it out'] },
  { key: 'love_language_trait', label: 'Love language', group: 'Personality', options: ['Words of affirmation', 'Acts of service', 'Receiving gifts', 'Quality time', 'Physical touch'] },
];

const GROUPS = ['Lifestyle', 'Background', 'Physical', 'Personality'];

const styles = {
  groupTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: 'var(--accent)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginTop: '20px',
    marginBottom: '12px',
    paddingBottom: '6px',
    borderBottom: '1px solid var(--border-color)',
  },
  row: {
    marginBottom: '16px',
  },
  label: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '6px',
  },
  labelText: {
    fontSize: '14px',
    fontWeight: '500',
  },
  visToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '11px',
    color: 'var(--text-muted)',
    cursor: 'pointer',
  },
};

export default function TraitsForm({ standalone = true, values, onChange }) {
  const { user, refreshUser } = useAuth();
  const [traits, setTraits] = useState({});
  const [visibility, setVisibility] = useState({});
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const source = values || user?.traits || [];
    const t = {};
    const v = {};
    for (const item of source) {
      try {
        t[item.trait_key] = JSON.parse(item.trait_value);
      } catch {
        t[item.trait_key] = item.trait_value;
      }
      v[item.trait_key] = item.visibility || 'public';
    }
    setTraits(t);
    setVisibility(v);
  }, [user, values]);

  const setTrait = (key, value) => {
    const next = { ...traits, [key]: value };
    setTraits(next);
    if (onChange) emitChange(next, visibility);
  };

  const toggleMulti = (key, option) => {
    const current = Array.isArray(traits[key]) ? traits[key] : [];
    const next = current.includes(option)
      ? current.filter((v) => v !== option)
      : [...current, option];
    setTrait(key, next);
  };

  const toggleVis = (key) => {
    const next = { ...visibility, [key]: visibility[key] === 'public' ? 'private' : 'public' };
    setVisibility(next);
    if (onChange) emitChange(traits, next);
  };

  const emitChange = (t, v) => {
    const arr = Object.entries(t)
      .filter(([, val]) => val && (!Array.isArray(val) || val.length > 0))
      .map(([key, val]) => ({
        trait_key: key,
        trait_value: Array.isArray(val) ? JSON.stringify(val) : val,
        visibility: v[key] || 'public',
      }));
    onChange(arr);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const arr = Object.entries(traits)
        .filter(([, val]) => val && (!Array.isArray(val) || val.length > 0))
        .map(([key, val]) => ({
          trait_key: key,
          trait_value: Array.isArray(val) ? val : val,
          visibility: visibility[key] || 'public',
        }));
      await api.put('/traits', { traits: arr });
      await refreshUser();
      setSuccess('Traits saved');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '16px', lineHeight: 1.5 }}>
        Describe yourself. These traits are used by our matching algorithm. Control visibility per-trait.
      </p>

      {GROUPS.map((group) => (
        <div key={group}>
          <h4 style={styles.groupTitle}>{group}</h4>
          {TRAIT_CATEGORIES.filter((c) => c.group === group).map((cat) => (
            <div key={cat.key} style={styles.row}>
              <div style={styles.label}>
                <span style={styles.labelText}>{cat.label}</span>
                <span style={styles.visToggle} onClick={() => toggleVis(cat.key)}>
                  {visibility[cat.key] === 'private' ? '🔒 Private' : '👁 Public'}
                </span>
              </div>
              <div className="chip-group">
                {cat.options.map((opt) => {
                  const isActive = cat.multiSelect
                    ? (Array.isArray(traits[cat.key]) && traits[cat.key].includes(opt))
                    : traits[cat.key] === opt;
                  return (
                    <button
                      key={opt}
                      className={`chip ${isActive ? 'active' : ''}`}
                      onClick={() =>
                        cat.multiSelect
                          ? toggleMulti(cat.key, opt)
                          : setTrait(cat.key, opt)
                      }
                      type="button"
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ))}

      {error && <p className="error-text" style={{ marginTop: '12px' }}>{error}</p>}
      {success && <p style={{ color: 'var(--success)', fontSize: '13px', marginTop: '12px' }}>{success}</p>}

      {standalone && (
        <button className="btn btn-primary" onClick={handleSave} disabled={saving} style={{ marginTop: '16px' }}>
          {saving ? 'Saving...' : 'Save traits'}
        </button>
      )}
    </div>
  );
}
